/**
 * The end of a task: the big code to keep, the details, and a QR to take it away on the phone.
 * Two looks: standard (a panel with rows) and card (one large coloured card, the Essenziale theme).
 */
import type { ReactNode } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { z } from 'zod';
import { defineWidget, type WidgetProps } from '../contract';
import { Icon } from '../../ui-kit/Icon';
import { Panel } from '../../ui-kit/Panel';
import css from '../Widgets.module.css';
import card from './TicketCard.module.css';

const schema = z.object({
  title: z.string(),
  subtitle: z.string().optional(),
  icon: z.string().default('done'),
  code: z.object({ label: z.string(), value: z.string() }).optional(),
  /** `wide`: a long value (a route) takes the whole row of the details. */
  lines: z.array(z.object({ label: z.string(), value: z.string(), icon: z.string().optional(), wide: z.boolean().default(false) })).default([]),
  qr: z.string().optional(),
  /** What the QR is, for whoever cannot see it. */
  qrLabel: z.string().default('QR'),
  note: z.string().optional(),
});

type Props = z.infer<typeof schema>;

/** Card: the code large on a coloured card, two details, the QR in the corner. */
function TicketCard({ props }: WidgetProps<Props>) {
  return <TicketCardView props={props} />;
}

/** The card look, with what a domain adds under the subtitle (`children`): the card is `position: relative`, so
 * a detail can cover it. */
export function TicketCardView({ props, children }: { props: Props; children?: ReactNode }) {
  return (
    <div className={card.stage}>
      <div className={card.card}>
        <span className={card.label}>{props.code?.label ?? props.title}</span>
        <strong className={card.code}>{props.code?.value ?? props.title}</strong>
        {props.subtitle && <p className={card.sub}>{props.subtitle}</p>}
        {children}
        <div className={card.bottom}>
          <dl className={card.lines}>
            {props.lines.slice(0, 3).map((l) => (
              <div key={l.label}><dt>{l.label}</dt><dd>{l.value}</dd></div>
            ))}
          </dl>
          {props.qr && (
            <span className={card.qr}>
              <QRCodeSVG value={props.qr} size={150} level="M" marginSize={1} title={props.qrLabel} role="img" aria-label={props.qrLabel} />
            </span>
          )}
        </div>
      </div>
      {props.note && <p className={card.note}>{props.note}</p>}
    </div>
  );
}

export const ticket = defineWidget({
  key: 'ticket',
  schema,
  variants: [{ id: 'card', label: 'widget.variant.card', Component: TicketCard }],
  Component: ({ props }) => (
    <Panel title={props.title} subtitle={props.subtitle} icon={props.icon}>
      <div className={css.ticket}>
        <div className={css.ticketMain}>
          {props.code && (
            <div className={css.code}>
              <span>{props.code.label}</span>
              <strong>{props.code.value}</strong>
            </div>
          )}
          <dl className={css.ticketLines}>
            {props.lines.map((l) => (
              <div key={l.label} className={`${css.ticketLine} ${l.wide ? css.wide : ''}`}>
                <dt>{l.icon && <Icon name={l.icon} size={24} />}{l.label}</dt>
                <dd>{l.value}</dd>
              </div>
            ))}
          </dl>
        </div>
        {props.qr && (
          <div className={css.qr}>
            <QRCodeSVG value={props.qr} size={200} level="M" marginSize={2} title={props.qrLabel} role="img" aria-label={props.qrLabel} />
          </div>
        )}
      </div>
      {props.note && <p className={css.footer}>{props.note}</p>}
    </Panel>
  ),
});
